import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_LANGUAGE,
  SERVICE_ALIASES,
  SERVICE_TRANSLATIONS,
  SUPPORTED_LANGUAGES,
} from '../i18n/catalog';

export const LanguageContext = React.createContext();

const STORAGE_KEY = 'selectedLanguage';

const resolveServiceName = (service = 'shared') =>
  SERVICE_ALIASES[service] || SERVICE_ALIASES[String(service || '').toLowerCase()] || 'shared';

const getNestedValue = (messages, key) => {
  if (!messages || !key) return undefined;

  return String(key).split('.').reduce((current, part) => {
    if (current == null || typeof current !== 'object') {
      return undefined;
    }

    return current[part];
  }, messages);
};

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const interpolate = (message, params = {}) => {
  if (typeof message !== 'string') return message;

  return Object.entries(params).reduce((result, [key, value]) => {
    const nextValue = String(value);
    const singleBracePlaceholder = `{${key}}`;
    const doubleBracePattern = new RegExp(`\\{\\{\\s*${escapeRegExp(key)}\\s*\\}\\}`, 'g');

    return result
      .split(singleBracePlaceholder)
      .join(nextValue)
      .replace(doubleBracePattern, nextValue);
  }, message);
};

const resolveMessage = (messages, key) => messages?.[key] ?? getNestedValue(messages, key);

const getMergedMessages = (service, language) => {
  const serviceName = resolveServiceName(service);
  const sharedMessages =
    SERVICE_TRANSLATIONS.shared?.[language] ||
    SERVICE_TRANSLATIONS.shared?.[DEFAULT_LANGUAGE] ||
    {};

  if (serviceName === 'shared') {
    return sharedMessages;
  }

  const serviceMessages =
    SERVICE_TRANSLATIONS[serviceName]?.[language] ||
    SERVICE_TRANSLATIONS[serviceName]?.[DEFAULT_LANGUAGE] ||
    {};

  return {
    ...sharedMessages,
    ...serviceMessages,
  };
};

const isSupportedLanguage = (language) =>
  Boolean(language) && SUPPORTED_LANGUAGES.includes(language);

const LanguageProvider = ({ children }) => {
  const storedLanguage = localStorage.getItem(STORAGE_KEY);
  const initialLanguage = isSupportedLanguage(storedLanguage)
    ? storedLanguage
    : DEFAULT_LANGUAGE;

  const [language, setLanguage] = useState(initialLanguage);

  const changeLanguage = useCallback((newLanguage) => {
    if (!isSupportedLanguage(newLanguage)) {
      console.warn(`Language ${newLanguage} not found in translations.`);
      return;
    }

    setLanguage(newLanguage);
  }, []);

  const getTranslate = useCallback(
    (service = 'shared') => {
      const messages = getMergedMessages(service, language);

      return (key, params = {}) => {
        const rawMessage = resolveMessage(messages, key) ?? key;
        return interpolate(rawMessage, params);
      };
    },
    [language]
  );

  const translate = useMemo(() => getTranslate('shared'), [getTranslate]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem(STORAGE_KEY);
    if (isSupportedLanguage(savedLanguage)) {
      setLanguage(savedLanguage);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
  }, [language]);

  const value = useMemo(
    () => ({
      language,
      translate,
      getTranslate,
      changeLanguage,
    }),
    [changeLanguage, getTranslate, language, translate]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;
